import { useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { ShieldCheck, ArrowRight, LockKeyhole } from "lucide-react";
import { useAdminAuth } from "@/context/AdminAuthContext";
import { formatApiErrorDetail } from "@/context/AuthContext";

export default function AdminLoginPage() {
    const { login, isAdminAuthenticated } = useAdminAuth();
    const navigate = useNavigate();
    const [username, setUsername] = useState("");
    const [password, setPassword] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [error, setError] = useState(null);

    if (isAdminAuthenticated) {
        return <Navigate to="/admin" replace />;
    }

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError(null);
        setSubmitting(true);
        try {
            await login({ username: username.trim(), password });
            toast.success("Signed in to admin console");
            navigate("/admin", { replace: true });
        } catch (err) {
            const detail = err?.response?.data?.detail || "Sign-in failed";
            const msg = formatApiErrorDetail(detail);
            setError(msg);
            toast.error(msg);
        } finally {
            setSubmitting(false);
        }
    };

    return (
        <div
            className="relative isolate flex min-h-screen items-center justify-center overflow-hidden bg-brand-charcoal px-6 py-16 text-brand-offwhite"
            data-testid="admin-login-page"
        >
            {/* Ambient background diagonals */}
            <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-20"
                style={{
                    backgroundImage:
                        "repeating-linear-gradient(45deg, transparent 0 24px, rgba(255,255,255,0.03) 24px 25px)",
                }}
            />
            <div className="pointer-events-none absolute -right-24 top-1/2 -translate-y-1/2 select-none font-display text-[220px] leading-none text-white/[0.03] md:text-[320px]">
                ADMIN
            </div>

            <div className="relative z-10 w-full max-w-md border border-white/10 bg-black/50 p-10 backdrop-blur">
                <img
                    src="/brand/logo-horizontal-dark.png?v=3"
                    alt="EB Construction"
                    className="mb-8 h-14 w-auto"
                />
                <div className="mb-8 flex items-center gap-3">
                    <div className="flex flex-col leading-none">
                        <p className="font-mono text-[10px] uppercase tracking-[0.3em] text-brand-gold">
                            Restricted access
                        </p>
                        <p className="mt-1 font-display text-2xl uppercase tracking-wide">
                            Admin Console
                        </p>
                    </div>
                </div>

                <div className="mb-6 flex items-center gap-2 border border-white/10 bg-white/[0.02] px-3 py-2 font-mono text-[10px] uppercase tracking-[0.2em] text-brand-offwhite/70">
                    <ShieldCheck className="h-3.5 w-3.5 text-brand-gold" />
                    Authorised operators only
                </div>

                <form onSubmit={handleSubmit} className="space-y-5" data-testid="admin-login-form">
                    <div>
                        <label
                            htmlFor="admin-username"
                            className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70"
                        >
                            Operator ID / email
                        </label>
                        <input
                            id="admin-username"
                            type="text"
                            autoComplete="username"
                            required
                            value={username}
                            onChange={(e) => setUsername(e.target.value)}
                            className="w-full border border-white/10 bg-white/[0.03] px-4 py-3 font-mono text-sm text-brand-offwhite placeholder:text-brand-offwhite/30 focus:border-brand-gold focus:outline-none"
                            placeholder="e.g. 1234567890 or your.email@example.com"
                            data-testid="admin-username-input"
                        />
                    </div>
                    <div>
                        <label
                            htmlFor="admin-password"
                            className="mb-2 block font-mono text-[10px] uppercase tracking-[0.25em] text-brand-offwhite/70"
                        >
                            Passphrase
                        </label>
                        <div className="relative">
                            <LockKeyhole className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-brand-offwhite/50" />
                            <input
                                id="admin-password"
                                type="password"
                                autoComplete="current-password"
                                required
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="w-full border border-white/10 bg-white/[0.03] py-3 pl-10 pr-4 font-mono text-sm text-brand-offwhite placeholder:text-brand-offwhite/30 focus:border-brand-gold focus:outline-none"
                                placeholder="••••••••••"
                                data-testid="admin-password-input"
                            />
                        </div>
                    </div>

                    {error && (
                        <p
                            className="border border-rose-400/40 bg-rose-500/10 px-3 py-2 font-mono text-xs text-rose-200"
                            data-testid="admin-login-error"
                        >
                            {error}
                        </p>
                    )}

                    <button
                        type="submit"
                        disabled={submitting}
                        className="group flex w-full items-center justify-center gap-3 bg-brand-gold px-6 py-3.5 font-mono text-xs uppercase tracking-[0.3em] text-brand-charcoal transition-transform hover:-translate-y-0.5 disabled:cursor-not-allowed disabled:opacity-60"
                        data-testid="admin-login-submit"
                    >
                        {submitting ? "Signing in…" : "Sign in"}
                        <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                    </button>
                </form>

                <p className="mt-8 border-t border-white/10 pt-6 font-mono text-[9px] uppercase leading-relaxed tracking-[0.2em] text-brand-offwhite/40">
                    ElevateBuild Construction Services Ltd · Admin Console v3.0
                </p>
            </div>
        </div>
    );
}
